import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasConsoleFlagQuery, isInternalConsolePath } from '../utils/productionTracking';
import { setPostLoginRedirect } from '../utils/postLoginRedirect';
import { safeInternalNext } from '../utils/safeNext';

/**
 * Sends a signed-out visitor from an internal console page (/admin, /marketing),
 * or from a URL a console page opened with an admin flag (adminReview,
 * adminOutreach or adminPreview equal to 1, next to company and negotiation
 * ids), to a bare /login. The attempted URL stays out of the address bar: these
 * URLs carry personal identifiers (provider and company ids, user ids, typed
 * place searches), and a /login page whose ?next= holds one is a tracked page
 * when it is reloaded or bookmarked, so GTM would relay it to Meta and Google.
 *
 * Login only honors a ?next= that safeInternalNext allows, which never includes
 * /admin, includes /marketing only for the overview email deep link, and
 * includes the provider area. Such a deep link rides in the post-login redirect
 * store (the same store Login captures ?next= into) so its landing is
 * unchanged. Every other attempted URL was ignored by Login, so dropping it
 * loses nothing: an admin still lands on /admin, a marketing user on
 * /marketing/overview.
 */
const ConsoleLoginRedirect = ({ attempted }) => {
  useEffect(() => {
    const deepLink = safeInternalNext(attempted);
    if (deepLink) setPostLoginRedirect(deepLink);
  }, [attempted]);
  return <Navigate to="/login" />;
};

/**
 * A component that restricts access to routes based on user roles
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {Array<string>} props.allowedRoles - List of roles allowed to access this route
 * @param {string} [props.redirectTo="/login"] - Where to redirect if unauthorized
 */
const RoleBasedRoute = ({ children, allowedRoles, redirectTo = "/login" }) => {
  const { isAuthenticated, auth, isLoading } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Safety timeout — if still loading after 5s, stop waiting
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show a brief loader while auth state initializes (max 5s)
  if (isLoading && !timedOut) {
    return <div>Loading authentication...</div>;
  }

  // If timed out or not authenticated, send the user to login carrying the
  // attempted URL. Login.js captures ?next= (validated by safeInternalNext,
  // which only allows intended provider paths and the marketing overview) and
  // returns the user there after OTP, so emailed deep links survive the login
  // wall. Other paths fail that allowlist and fall back to the role's default.
  // Console paths and admin-flag URLs never ride in the query string (see
  // ConsoleLoginRedirect).
  if (!isAuthenticated) {
    if (isInternalConsolePath(location.pathname) || hasConsoleFlagQuery(location.search)) {
      return <ConsoleLoginRedirect attempted={location.pathname + location.search} />;
    }
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
      />
    );
  }

  // Handle case where token exists but user info might not be fully loaded yet
  if (!auth?.user) {
    return <Navigate to={redirectTo} />;
  }

  // Get the user's role from the token
  const userRole = auth.user.role;

  // Special case for ADMIN role - they can access everything
  if (userRole === 'ADMIN') {
    return children;
  }

  // Check if the user's role is in the allowed roles list
  // Older provider tokens used the generic PROVIDER role. Login and signed-in
  // landing already treat it as CRNA-compatible; normalize it here too so a
  // preserved MCP service draft cannot be redirected to /unauthorized after
  // an otherwise successful legacy-account sign-in.
  const routeRole = userRole === 'PROVIDER' ? 'CRNA' : userRole;
  const isAuthorized = allowedRoles.includes(routeRole);

  if (!isAuthorized) {
    // Redirect to unauthorized page if not authorized
    return <Navigate to="/unauthorized" />;
  }

  return children;
};

export default RoleBasedRoute;
